import { FlexFee2Icon } from "@xola/icons";
import { almostZero, flash, Spinner, Tooltip } from "@xola/ui-kit";
import { AxiosError, AxiosRequestConfig } from "axios";
import clsx from "clsx";
import {
    getAddOnLimitError,
    getAddOns,
    getPaymentLineItems,
    getSortedAddonAndDemographicLineItems,
    getSortedLineItems,
    isAdjustmentLineItem,
    isEveryItemCanceled,
    isGift,
    isOtherSellerPurchase,
    isVisibleToSeller,
    SummarySkeleton,
} from "common";
import { capitalize, isEqual, sumBy } from "lodash";
import React, { FC, ReactElement, useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
    CatalogType,
    Experience,
    LineItem,
    LineItemObject,
    LineItemType,
    LineItemVisibility,
    Permission,
    Product,
    ProductCatalogItem,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
} from "types";
import { useDebounce } from "use-debounce";
import { api } from "../../api/api";
import { findValue, hasInProductFilterNotMetError } from "../../api/interceptors";
import { useSeller } from "../../hooks/useAccount";
import { useResource } from "../../hooks/useResource";
import {
    getItemTotal,
    getPartnerFee,
    getPartnerFeeByTraveler,
    getPartnerPackageItemLineItem,
    getPartnerPurchaseItemLineItems,
    hasCouponEligibleItems,
    isPackage,
} from "../../models/Purchase";
import { isBookingEngine } from "../../models/PurchaseItem";
import { CreatePurchaseItemState } from "../../stores/createPurchaseStore";
import { Breakdown } from "../Common/Breakdown";
import { getValueWithCurrency } from "../Common/Currency";
import { IsGranted } from "../Common/IsGranted";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { SourceIcon, SourceName } from "../Common/SourceIcon";
import { BalanceDue } from "./BalanceDue";
import { DiscountLineItem } from "./DiscountLineItem";
import { hasModifyPaymentOptions, ModifyPaymentPopover } from "./ModifyPaymentPopover";
import { AddonLineItem } from "./PaymentBreakdown/AddonLineItem";
import { AdjustmentLineItem } from "./PaymentBreakdown/AdjustmentLineItem";
import { CouponLineItem } from "./PaymentBreakdown/CouponLineItem";
import { DefaultLineItem } from "./PaymentBreakdown/DefaultLineItem";
import { PartnerFeeLineItem } from "./PaymentBreakdown/PartnerFeeLineItem";
import { PaymentLineItem } from "./PaymentBreakdown/PaymentLineItem";
import { RefundLineItem } from "./PaymentBreakdown/RefundLineItem";
import { canShowPaidDetails } from "./PaymentBreakdown/utils";
import { showFlexStandardServiceFeeToSeller } from "@/models/PartnerFeeFormula";

interface PurchaseBreakdown {
    showTitles?: boolean;
    purchase: Purchase;
    item?: PurchaseItem | CreatePurchaseItemState;
    payload?: any;
    isEnabled?: boolean;
    isEditable?: boolean;
    onApplyCode?: () => void;
    onError?: (error: AxiosError) => void;
    /** Receives an exceeded add-on limit instead of the default error message. */
    onAddOnLimitError?: (error: AxiosError) => void;
    onUpdate?: (response: Purchase | null) => void;
    isUpdating?: (isUpdating: boolean) => void;
    isParentLoading?: boolean;
    validationLevel?: string;
}

export const PaymentBreakdown = ({
    showTitles = true,
    purchase: originalPurchase,
    item,
    payload,
    isEnabled = true,
    isEditable = false,
    onApplyCode,
    onError,
    onAddOnLimitError,
    onUpdate,
    isUpdating,
    isParentLoading,
    validationLevel,
}: PurchaseBreakdown) => {
    const seller = useSeller();
    const [debouncedPayload] = useDebounce(payload, 500);
    const [showPopoverId, setPopoverId] = useState([""]);

    const [initialPartnerFeeAmount] = useState(
        originalPurchase.lineItems.find((item) => item.type === LineItemType.PARTNER_FEE)?.amount,
    );

    const isDifferentSellerPurchase = isOtherSellerPurchase(originalPurchase, seller.id);
    const isReadOnlyPartnerPurchase = isDifferentSellerPurchase && !originalPurchase.partner?.editPurchases;

    const handlePurchaseError = (error: AxiosError) => {
        // The query stays on the debounced payload, so a request for a payload the user has since edited can fail
        // after the edit and would report an error for values no longer shown.
        if (!isEqual(debouncedPayload, payload)) {
            return;
        }

        if (onAddOnLimitError && getAddOnLimitError(error)) {
            onAddOnLimitError(error);
            return;
        }

        if (error?.response?.status === 400 && onError) {
            onError(error);
        } else {
            // Need to handle error message if its related to line item name
            const data: any = error.response?.data;
            if (data?.field && Object.keys(data.field)[0].includes("name")) {
                return flash.danger("Line Item Name should not be blank");
            }

            flash.danger(getErrorMessage(error));
        }
    };

    const {
        data: previewPurchase,
        isFetching,
        isLoading,
    } = useQuery<any, AxiosError, Purchase>(
        ["dryRun", "purchases", originalPurchase.id, debouncedPayload, seller.id, validationLevel],
        async () => updatePurchaseDryRun(originalPurchase.id, debouncedPayload, seller.id, validationLevel, !onError),
        {
            staleTime: 1500,
            keepPreviousData: true,
            onSuccess: onUpdate,
            enabled: isEnabled && !!debouncedPayload && isEqual(debouncedPayload, payload),
            onError: handlePurchaseError,
        },
    );
    const { data: experience } = useResource<Experience>(["products", item?.product?.id]);

    useEffect(() => {
        if (typeof isUpdating !== "undefined") {
            isUpdating(isFetching || isLoading);
        }
    }, [isUpdating, isFetching, isLoading]);

    const purchase = previewPurchase ?? originalPurchase;
    const isMist = purchase.items.length > 1;
    const isStoreCredit = purchase.items.some((item) => item.object === PurchaseItemObject.STORE_CREDIT);
    const isCouponEligible = hasCouponEligibleItems(purchase);
    let showApplyCode = isEditable && !isStoreCredit && !isEveryItemCanceled(purchase) && isCouponEligible;

    const currentItem = item && purchase.items.find((newItem) => newItem.id === item.id);
    const items = currentItem ? [currentItem] : purchase.items;
    if (isReadOnlyPartnerPurchase || purchase.seller.id !== seller.id) {
        showApplyCode = false;
    }
    const experienceAddons = experience ? getAddOns(experience) : [];
    const choicesAddOns = getAllChoicesAddOns(experienceAddons);

    const partnerFee = getPartnerFee(purchase);
    const partnerFeeByTraveler = getPartnerFeeByTraveler(purchase, false);
    const paymentLineItems = getPaymentLineItems(purchase);
    const filteredPaymentLineItems = paymentLineItems.filter(
        (lineItem) => !(lineItem.type === LineItemType.REFUND_COMMISSION && lineItem.amount === 0),
    );
    const isFlexLevelPlan =
        partnerFeeByTraveler?.includeInAmount && partnerFeeByTraveler?.visibility === LineItemVisibility.ALL;
    const showPartnerFees = partnerFeeByTraveler && !isFlexLevelPlan;
    const showConvenienceFee = showPartnerFees && showFlexStandardServiceFeeToSeller(seller);

    const paid = -1 * sumBy(filteredPaymentLineItems, "amount");

    const partnerPackageItemLineItem = getPartnerPackageItemLineItem(purchase);
    const partnerPurchaseItemLineItems = getPartnerPurchaseItemLineItems(purchase);
    const partnerLineItems = partnerPackageItemLineItem
        ? [partnerPackageItemLineItem, ...partnerPurchaseItemLineItems]
        : partnerPurchaseItemLineItems;

    const handleModifyPopover = (id: string) => {
        if (showPopoverId.includes(id)) {
            setPopoverId(showPopoverId.filter((popoverId: string) => popoverId !== id));
            return;
        }

        setPopoverId([...showPopoverId, id]);
    };

    return (
        <div>
            {(isLoading || isParentLoading) && currentItem ? (
                <div className="h-72 space-y-2">
                    <div className="pb-4 text-lg font-bold text-secondary">Purchase Summary</div>
                    <SummarySkeleton item={currentItem} />
                </div>
            ) : (
                <div data-testid="payment-breakdown" className="w-full space-y-4">
                    <div className="flex items-center text-lg font-bold">
                        {showTitles && <span>Purchase Summary</span>}
                        {showPartnerFees && (
                            // If there is a partner fee but it's not visible to the seller, show an icon with a tooltip
                            // Otherwise, there will be a partner fee line item in the breakdown below
                            <Tooltip
                                content={
                                    <NoTranslate>
                                        {showConvenienceFee &&
                                            `${getValueWithCurrency(partnerFeeByTraveler.amount, purchase.currency)} `}
                                        {partnerFeeByTraveler.name}
                                    </NoTranslate>
                                }
                            >
                                <FlexFee2Icon size="medium" className="ml-2" />
                            </Tooltip>
                        )}
                        {item?.source && isBookingEngine(item.source) && (
                            <Tooltip content={<SourceName source={item.source} />}>
                                <SourceIcon
                                    source={item.source}
                                    className="ml-2 rounded-none object-fill"
                                    size="large"
                                />
                            </Tooltip>
                        )}
                        {isFetching ? <Spinner size="tiny" className="ml-2" /> : null}
                    </div>
                    {items.map((currentItem, index) => {
                        const mistItemSelected = isMist && item !== undefined;
                        const isLast = index + 1 === items.length;
                        const showPayments = isLast && (!mistItemSelected || isPackage(currentItem));
                        const lineItems = getSortedLineItems(currentItem);
                        const itemTotal = getItemTotal(purchase, currentItem);
                        const isGiftItem = isGift(currentItem);
                        const showModify =
                            isEditable &&
                            !isStoreCredit &&
                            !isGiftItem &&
                            !isReadOnlyPartnerPurchase &&
                            hasModifyPaymentOptions(purchase, currentItem);
                        const initialItem = originalPurchase.items.find((item) => item.id === currentItem.id);

                        const itemToCompare = (
                            initialItem && "lineItems" in initialItem ? initialItem : item
                        ) as PurchaseItem;

                        return (
                            <div key={currentItem.id}>
                                <Breakdown currency={purchase.currency}>
                                    {isMist && currentItem.product && (
                                        <tr>
                                            <td colSpan={2}>
                                                <ProductName id={currentItem.product.id} />
                                            </td>
                                        </tr>
                                    )}

                                    {getSortedAddonAndDemographicLineItems(lineItems).map((lineItem) => (
                                        <BreakdownLineItem
                                            key={lineItem.id}
                                            purchase={purchase}
                                            choicesAddOns={choicesAddOns}
                                            lineItem={lineItem}
                                            item={itemToCompare}
                                        />
                                    ))}

                                    {partnerFee && !isMist && (
                                        <BreakdownLineItem
                                            key={partnerFee.id}
                                            purchase={purchase}
                                            lineItem={partnerFee}
                                            initialPartnerFeeAmount={initialPartnerFeeAmount}
                                        />
                                    )}
                                    <Breakdown.Separator />
                                    <Breakdown.SubtotalItem info="Total" value={itemTotal}>
                                        {showModify && (
                                            <>
                                                <IsGranted permissions={Permission.MODIFY_BOOKING_LITE}>
                                                    <SecondaryButton
                                                        size="small"
                                                        className="mt-1"
                                                        onClick={() => handleModifyPopover(currentItem.id)}
                                                    >
                                                        Modify
                                                    </SecondaryButton>
                                                </IsGranted>
                                                <ModifyPaymentPopover
                                                    purchase={purchase}
                                                    isVisible={showPopoverId.includes(currentItem.id)}
                                                    item={currentItem}
                                                    onClose={(id: string) => handleModifyPopover(id)}
                                                />
                                            </>
                                        )}
                                    </Breakdown.SubtotalItem>
                                </Breakdown>

                                {isLast && partnerFee && isMist && (
                                    <div className="mb-6 py-6">
                                        <Breakdown currency={purchase.currency}>
                                            <BreakdownLineItem
                                                key={partnerFee.id}
                                                purchase={purchase}
                                                lineItem={partnerFee}
                                                initialPartnerFeeAmount={initialPartnerFeeAmount}
                                            />
                                        </Breakdown>
                                    </div>
                                )}

                                {isLast && isMist && !mistItemSelected && (
                                    <Breakdown currency={purchase.currency}>
                                        <Breakdown.Separator />
                                        <div className="mb-4 border-b  border-gray-light pt-3 text-2xl">
                                            <Breakdown.SubtotalItem value={purchase.amount}>
                                                Grand Total
                                            </Breakdown.SubtotalItem>
                                        </div>
                                    </Breakdown>
                                )}

                                {partnerLineItems && (
                                    <Breakdown currency={purchase.currency}>
                                        <Breakdown.Separator />
                                        {partnerLineItems?.map((partnerLineItem, count) => (
                                            <div
                                                key={partnerLineItem.id}
                                                className={clsx(
                                                    "border-b border-gray-light pt-3",
                                                    count === partnerLineItems.length - 1 && "mb-4",
                                                )}
                                            >
                                                <Breakdown.SubtotalItem value={partnerLineItem.amount}>
                                                    {capitalize(partnerLineItem.name)}
                                                </Breakdown.SubtotalItem>
                                            </div>
                                        ))}
                                    </Breakdown>
                                )}

                                {showPayments && (
                                    <>
                                        <Breakdown currency={purchase.currency}>
                                            {filteredPaymentLineItems.length > 0 ? (
                                                <tr>
                                                    <td
                                                        className="whitespace-nowrap pb-6 text-lg font-bold"
                                                        colSpan={3}
                                                    >
                                                        {showTitles && "Payment Summary"}
                                                        {isFetching ? <Spinner size="tiny" className="ml-2" /> : null}
                                                    </td>
                                                </tr>
                                            ) : null}

                                            {filteredPaymentLineItems.map((lineItem) => {
                                                return (
                                                    <BreakdownLineItem
                                                        key={lineItem.id}
                                                        purchase={purchase}
                                                        lineItem={lineItem}
                                                    />
                                                );
                                            })}

                                            {paymentLineItems.length > 0 && <Breakdown.Separator />}

                                            {canShowPaidDetails(items, paid) && (
                                                <PaidSubtotalItem
                                                    isPayLater={paymentLineItems.length === 0}
                                                    value={paid}
                                                >
                                                    {showApplyCode ? (
                                                        <IsGranted permissions={Permission.MODIFY_BOOKING_LITE}>
                                                            <SecondaryButton
                                                                size="small"
                                                                className="mt-1"
                                                                onClick={onApplyCode}
                                                            >
                                                                Apply Code
                                                            </SecondaryButton>
                                                        </IsGranted>
                                                    ) : null}
                                                </PaidSubtotalItem>
                                            )}
                                        </Breakdown>
                                        {!almostZero(purchase.balance) &&
                                            !(isPackage(currentItem) && purchase.balance < 0) && (
                                                <BalanceDue purchase={purchase} item={item} />
                                            )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PaidSubtotalItem = ({
    value: paid,
    isPayLater,
    children,
}: {
    value: number;
    isPayLater: boolean;
    children: ReactElement | null;
}) => {
    if (isPayLater) {
        return <div className="pt-1 pb-4">{children}</div>;
    }

    return (
        <Breakdown.SubtotalItem info="Paid" value={paid}>
            {children}
        </Breakdown.SubtotalItem>
    );
};

export const ProductName = ({ id }: { id: ID }) => {
    const { data: product, isLoading } = useResource<Product>(["products", id]);

    return isLoading ? null : (
        <NoTranslate as="div" className="text-md font-bold" data-testid="payment-breakdown-product-name">
            {product?.name ?? "N/A"}
        </NoTranslate>
    );
};

const updatePurchaseDryRun = async (
    id: ID,
    payload: any,
    seller: string,
    validationLevel?: string,
    suppressErrorToast = false,
) => {
    if (payload.ops && (payload.ops[0]?.data.amount === 0 || (payload.ops[1] && payload.ops[1].data.amount === 0))) {
        return null;
    }

    const params: any = { dryRun: true, seller };

    if (validationLevel !== "") {
        params.validationLevel = validationLevel;
    }

    const { data } = await api.patch(`/purchases/${id}`, payload, {
        params: params,
        skipResponseInterceptor: true,
        /** Bad request error toasters would be handled by this component itself */
        suppressErrorToast: suppressErrorToast ? [400] : undefined,
    } as AxiosRequestConfig);
    return data;
};

// All line items components should use this prop type.
interface LineItemProps {
    purchase: Purchase;
    lineItem: LineItem;
    item?: PurchaseItem;
    initialPartnerFeeAmount?: number;
    choicesAddOns?: ProductCatalogItem[];
}

/**
 * Main line item component that renders the line item by type defined in `lineItemMap`.
 */
export const BreakdownLineItem = ({ lineItem, choicesAddOns, ...rest }: LineItemProps) => {
    const lineItemChoiceCatalogName = choicesAddOns
        ? choicesAddOns.find((addOn) => {
              return addOn?.items?.find((item) => {
                  return item?.template?.id === (lineItem?.template?.id ?? "");
              });
          })?.name
        : null;

    if (!isVisibleToSeller(lineItem)) {
        return null;
    }

    if (
        [
            LineItemType.ADDON,
            LineItemType.PICKUP,
            LineItemType.DEMOGRAPHIC,
            LineItemType.EQUIPMENT,
            LineItemType.MEMBERSHIP,
            LineItemType.WARE,
        ].includes(lineItem.type) &&
        lineItem.quantity === 0 &&
        lineItem.amount === 0
    ) {
        return null;
    }

    if (
        lineItem.amount === 0 &&
        [LineItemType.TAX, LineItemType.FEE, LineItemType.PARTNER_FEE].includes(lineItem.type)
    ) {
        return null;
    }

    if (lineItem.type === LineItemType.ADDON) {
        if (lineItem.quantity === 0) {
            return null;
        }

        return <AddonLineItem lineItem={lineItem} lineItemChoiceCatalogName={lineItemChoiceCatalogName} {...rest} />;
    }

    if (isAdjustmentLineItem(lineItem)) {
        return <AdjustmentLineItem lineItem={lineItem} />;
    }

    if (lineItem.type === LineItemType.PARTNER_FEE) {
        return (
            <PartnerFeeLineItem
                lineItem={lineItem}
                initialPartnerFeeAmount={rest.initialPartnerFeeAmount ?? 0}
                {...rest}
            />
        );
    }

    if (lineItem.type === LineItemType.DISCOUNT) {
        return <DiscountLineItem lineItem={lineItem} item={rest.item} />;
    }

    if (lineItem.object === LineItemObject.DEFAULT) {
        return <DefaultLineItem lineItem={lineItem} lineItemChoiceCatalogName={lineItemChoiceCatalogName} {...rest} />;
    }

    const LineItemComponent = lineItemMap[lineItem.object] ?? DefaultLineItem;
    return <LineItemComponent lineItem={lineItem} {...rest} />;
};

const getAllChoicesAddOns = (addOns: ProductCatalogItem[]) => {
    return addOns.filter((addOn) => {
        return addOn.type === CatalogType.CHOICES;
    });
};

const getErrorMessage = (error: AxiosError) => {
    const inProductFilterNotMetError = hasInProductFilterNotMetError(error);
    if (inProductFilterNotMetError.error && inProductFilterNotMetError.isMembershipError) {
        return "This product does not meet the coupon restrictions";
    }

    return findValue(error?.response?.data, "message") ?? "Error fetching purchase information";
};

const lineItemMap: Record<LineItemObject, FC<LineItemProps>> = {
    [LineItemObject.REFUND]: RefundLineItem,
    [LineItemObject.PAYMENT]: PaymentLineItem,
    [LineItemObject.DEFAULT]: DefaultLineItem,
    [LineItemObject.COUPON]: CouponLineItem,
    [LineItemObject.COUPON_REDEEM]: DefaultLineItem,
    [LineItemObject.DISCOUNT_CODE]: DefaultLineItem,
    [LineItemObject.CODE_REDEEM]: DefaultLineItem,
};

export const enum PriceStrategy {
    RETAIN = "retain",
    LATEST = "latest",
}
